import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { axiosRequest } from "../../../service/AxiosService";
import {
  EditMailTemplateRequest,
  ToUser,
} from "../../../type/request/EditMailTemplateRequest";
import MailBodyComponent from "./MailBodyComponent";

/** フォームのデフォルト値 */
const formDefaultValues: EditMailTemplateRequest = {
  id: "",
  name: "",
  description: "",
  subject: "",
  body: "",
  toUsers: [],
};

/**
 * メールテンプレート登録・更新ダイアログコンポーネント
 * - テンプレートコード・名前・件名・本文（HTML）・宛先ユーザーを編集できる
 * - 「コピー登録」で現在のデータを新規テンプレートとして複製できる
 * - props: open (ダイアログ表示状態), onClose (閉じるコールバック),
 *          data (編集対象データ、未指定時は新規作成), allMailTemplate (重複チェック用全テンプレート),
 *          selectToUsers (宛先候補ユーザー一覧)
 */
const EditMailTemplateDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  data?: EditMailTemplateRequest;
  allMailTemplate: EditMailTemplateRequest[];
  selectToUsers: ToUser[];
}> = ({ open, onClose, data, allMailTemplate, selectToUsers }) => {
  const form = useForm<EditMailTemplateRequest>({
    mode: "onSubmit",
    defaultValues: formDefaultValues,
  });
  const {
    control,
    reset,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = form;
  const bodyRef = useRef<any>(null);
  const [bodyRequired, setBodyRequired] = useState("");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  /** 初期処理 */
  useEffect(() => {
    if (data) {
      reset({ ...data, toUsers: data.toUsers ?? [] });
      if (bodyRef.current) {
        bodyRef.current.setValue(data.body);
      }
    } else {
      reset(formDefaultValues);
      if (bodyRef.current) {
        bodyRef.current.setValue("");
      }
    }
    setBodyRequired("");
  }, [data, reset]);

  /** メール本文の初期値設定 */
  const bodyDefaultValue = useMemo(() => {
    return data ? data.body : "";
  }, [data]);

  /** 本文バリデーションチェック */
  const handleSetBodyError = () => {
    setBodyRequired(
      bodyRef.current?.getValue() ? "" : "この項目は入力必須です。"
    );
    return !!bodyRef.current?.getValue();
  };

  /** メールテンプレート登録・更新処理 */
  const handleSave = async () => {
    if (handleSetBodyError()) {
      const requestData: EditMailTemplateRequest = {
        ...getValues(),
        body: bodyRef.current?.getValue() ?? "",
        toUsers:
          (getValues("toUsers")?.length ?? 0) > 0
            ? getValues("toUsers")
            : undefined,
      };
      try {
        const response = await axiosRequest<string>(
          "/mail/editMailTemplate",
          "POST",
          { data: requestData }
        );
        if (response) {
          onClose();
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>メールテンプレート登録・更新</DialogTitle>
        <DialogContent>
          <Box
            sx={{ display: "flex", flexDirection: "column", rowGap: 2, mt: 1 }}
          >
            <Grid container spacing={2}>
              <Grid size={6}>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: "この項目は入力必須です。",
                    validate: (newValue) => {
                      if (
                        allMailTemplate.some(
                          (v) => v.id !== getValues("id") && v.name === newValue
                        )
                      ) {
                        return "このテンプレート名はすでに登録されています。";
                      }
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      label="テンプレート名"
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="説明" fullWidth />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  name="toUsers"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={selectToUsers}
                      getOptionLabel={(option) =>
                        `${option.lastName} ${option.middleName ?? ""} ${option.firstName}`
                      }
                      value={field.value ?? []}
                      onChange={(_, value) => field.onChange(value)}
                      multiple
                      renderInput={(params) => (
                        <TextField {...params} label="宛先" fullWidth />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  name="subject"
                  control={control}
                  rules={{ required: "この項目は入力必須です。" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      label="件名"
                      fullWidth
                      error={!!errors.subject}
                      helperText={errors.subject?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <MailBodyComponent
              bodyRef={bodyRef}
              defaultValue={bodyDefaultValue}
              height={400}
              required={{ value: true, message: bodyRequired }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Button variant="outlined" onClick={onClose}>
              キャンセル
            </Button>
            <Box
              sx={{
                display: "flex",
                columnGap: 1,
              }}
            >
              {data && (
                <Button
                  variant="outlined"
                  onClick={() => setOpenConfirmDialog(true)}
                >
                  コピー登録
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleSubmit(handleSave, handleSetBodyError)}
              >
                保存
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
      {/* コピー登録確認ダイアログ */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
      >
        <DialogTitle>コピー登録確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            新規メールテンプレートとして登録します。よろしいですか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setOpenConfirmDialog(false)}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setValue("id", "");
                handleSubmit(handleSave, handleSetBodyError)();
                setOpenConfirmDialog(false);
              }}
            >
              登録
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditMailTemplateDialog;
